import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { FlexRow } from 'ui-toolkit/components/FlexRow/FlexRow';
import { RotatingIcon } from './RotatingIcon';
import { IconType } from 'ui-toolkit/components/Icon';
import { SwitchDialogWindows } from 'ui-toolkit/components/SwitchDialogWindows/SwitchDialogWindows';
import { Notification } from 'ui-toolkit/components/Notification/Notification';
import { BodyText, BoldBodyText, Grid } from 'ui-toolkit';
import { Select } from 'ui-toolkit/components/Select/Select';
import { ModalWindow } from 'ui-toolkit/components/ModalWindow/ModalWindow';
import styled from 'styled-components';
import { Button } from 'ui-toolkit/components/Button/Button';
import { useDocumentsContext } from '../DocumentsProvider';
import { DocumentData } from '../shared/DocumentData';
import { categoryForDisplay, isDocumentReadyToUpload } from 'types/order/documents/shared/utilities';
import { IMetadata } from 'types/order/documents/shared/interfaces';
import { IUploadDocument } from 'types/order/documents/upload/interfaces';
import { DocumentModalTitle } from '../shared/DocumentModalTitle';
import { IDocumentCategory, requiresGoodThroughDate } from '../../../types/order/documents/IDocumentCategoryType';
import { createDocument } from '../service';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../API/documentCategories/DocumentCategoriesApi';

const StyledDocumentFile = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5} 0;
`;

const StyledModalContent = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
`;

export interface IUploadDocumentProps {
  document: IUploadDocument;
  documentCategories: IDocumentCategory[];
}

export const UploadDocument: React.FC<IUploadDocumentProps> = ({ document, documentCategories }) => {
  const [
    {
      upload: { isLoading, isComplete, hasError },
      shared: { standardDocuments },
    },
    dispatch,
  ] = useDocumentsContext();
  const { data: { documentCategoriesByName } = defaultNormalizedDocumentCategoriesData } =
    useListDocumentCategoriesQuery();

  const showModal = requiresGoodThroughDate(document.category, documentCategoriesByName);

  const setUiAndUploadDocument = useCallback(
    async (uploadDocument: IUploadDocument) => {
      dispatch({ type: 'SetUploadIsLoading', payload: true });
      try {
        await createDocument(uploadDocument);
        setTimeout(() => window.location.reload(), 1000);
      } catch (e: any) {
        dispatch({ type: 'SetUploadHasError', payload: true });
      }
      dispatch({ type: 'SetUploadIsLoading', payload: false });
      dispatch({ type: 'SetUploadIsComplete', payload: true });
    },
    [dispatch]
  );
  const onCloseModal = () => {
    //FYI: clearing document closes modal and resets view
    dispatch({ type: 'DeleteUploadDocument', payload: document.id });
  };
  const onDocumentCategoryFilter = (option: { label: string; value: string }, rawInput: string) =>
    rawInput
      ? option.label.toLowerCase().includes(rawInput.toLowerCase()) ||
        option.label
          .replace(/[^A-Z]/g, '')
          .toLowerCase()
          .includes(rawInput.toLowerCase())
      : true;
  const onDocumentCategorySelect = (option: IDocumentCategory) => {
    const match = standardDocuments.find(
      (sd) =>
        !!sd.metadata &&
        !!sd.metadata.original_file_name &&
        sd.metadata.original_file_name === document.file.name &&
        sd.category === option.name
    );

    const newDocument = {
      ...document,
      category: option.name,
      isRepeatable: option.isRepeatable,
      metadata: match && !option.isRepeatable ? match.metadata : undefined,
    } as IUploadDocument;
    dispatch({ type: 'SetUploadDocument', payload: newDocument });
  };

  useEffect(() => {
    if (
      documentCategoriesByName &&
      document.category &&
      !requiresGoodThroughDate(document.category, documentCategoriesByName)
    ) {
      setUiAndUploadDocument(document);
    }
  }, [document, setUiAndUploadDocument, documentCategoriesByName]);

  const onDocumentChange = (metadata: IMetadata) =>
    dispatch({
      type: 'SetUploadDocumentMetadata',
      payload: {
        ...document,
        metadata,
      },
    });
  const onFinishUpload = () => setUiAndUploadDocument(document);
  return (
    <StyledDocumentFile>
      {!isComplete ? (
        <BodyText>
          Select a Category for <BoldBodyText as="span">{document.file.name}</BoldBodyText>
        </BodyText>
      ) : null}
      <div>
        {showModal || (!isLoading && !isComplete) ? (
          <Select
            filterOption={onDocumentCategoryFilter}
            options={documentCategories}
            onChange={onDocumentCategorySelect}
            placeholder="Start typing to select a category"
            value={!!document.category ? documentCategories.find((sdc) => sdc.name === document.category) : null}
          />
        ) : isLoading ? (
          <FlexRow>
            <RotatingIcon type={IconType.Loading} />
            <BodyText>Uploading your file...</BodyText>
          </FlexRow>
        ) : !hasError ? (
          <Notification type="success">Your file has been uploaded. Refreshing the page...</Notification>
        ) : (
          <Notification type="error">Your file could not be uploaded.</Notification>
        )}
      </div>
      {showModal ? (
        <SwitchDialogWindows
          isLoading={isLoading}
          isCompleted={isComplete}
          isError={hasError}
          loadingTitle="Uploading your file..."
          successTitle="Your file has been uploaded. Refreshing the page..."
          errorTitle="Your file could not be uploaded."
          onCloseClick={onCloseModal}
        >
          <ModalWindow
            contentJustifyContent={'start'}
            contentTextAlign={'left'}
            minHeight={400}
            onClose={onCloseModal}
            title={
              <DocumentModalTitle superText={document.file.name}>
                Finish uploading {categoryForDisplay(documentCategories, document.category!)}
              </DocumentModalTitle>
            }
            uitype="fit"
            width={580}
          >
            <StyledModalContent>
              {!isLoading ? (
                <>
                  <DocumentData
                    document={document}
                    onChange={onDocumentChange}
                    showInputs={true}
                    documentCategoriesByName={documentCategoriesByName}
                  />
                  <Grid columnCount={2} columnWidth={'max-content'}>
                    <Button
                      intent={'primary'}
                      onClick={onFinishUpload}
                      disabled={!isDocumentReadyToUpload(document, documentCategoriesByName)}
                    >
                      Finish Upload
                    </Button>
                    <Button intent={'secondary'} onClick={onCloseModal}>
                      Cancel
                    </Button>
                  </Grid>
                </>
              ) : (
                <FlexRow>
                  <RotatingIcon type={IconType.Loading} />
                  <BodyText>Uploading</BodyText>
                </FlexRow>
              )}
            </StyledModalContent>
          </ModalWindow>
        </SwitchDialogWindows>
      ) : null}
    </StyledDocumentFile>
  );
};
